<template>
  <div class="group-container">
    <div class="group-name-row">
      <input class="group-name" placeholder="Name your group" type="text" :value="name" @input="handleInput" />
      <div v-if="totalGroups > 1">
        <img src="@/assets/images/3dots.png" class="group-3-dots" alt="more" @click="openGroupMenu" />
        <div
          v-show="groupMenuVisible"
          ref="groupMenu"
          class="group-menu"
          tabindex="-1"
          @blur="groupMenuVisible = false"
        >
          <div class="team-item list" @click="deleteGroup(groupId)">
            <span class="team-item-name">
              Delete group
            </span>
          </div>
        </div>
      </div>
    </div>
    <div class="group-separator"></div>
    <div v-for="member in members" :key="member.id" class="team-item">
      <div class="team-item-avatar" :style="'background-color: ' + member.avatarColor">
        {{ `${member.firstName[0]}${member.lastName[0]}` }}
      </div>
      <div class="team-item-name">
        {{ `${member.firstName} ${member.lastName}` }}
      </div>
      <div class="team-item-delete" @click="$emit('delete-member', groupId, member.id)">
        &times;
      </div>
    </div>
    <div class="group-add-team">
      <button
        class="group-add-team-button"
        :class="{ 'disabled': !name || availableMembers.length === 0 }"
        :disabled="!name || availableMembers.length === 0"
        @click="toggleAvailableMembers"
      >
        + Add team member
      </button>
      <div v-if="showList" class="team-list">
        <div v-for="user in availableMembers" :key="user.id" class="team-item list" @click="addMember(user.id)">
          <div class="team-item-avatar" :style="'background-color: ' + user.avatarColor">
            {{ `${user.firstName[0]}${user.lastName[0]}` }}
          </div>
          <div class="team-item-name">
            {{ `${user.firstName} ${user.lastName}` }}
          </div>
        </div>
      </div>
    </div>
    <div v-if="error" class="error-message">
      {{ error }}
    </div>
  </div>
</template>

<script>
export default {
  name: 'GroupContainer',
  props: {
    groupId: {
      required: true,
      type: [Number, String]
    },
    isCreated: {
      required: true,
      type: Boolean
    },
    name: {
      required: true,
      type: String
    },
    error: {
      default: () => '',
      required: false,
      type: String
    },
    team: {
      required: true,
      type: Array
    },
    members: {
      required: true,
      type: Array
    },
    totalGroups: {
      required: true,
      type: Number
    }
  },
  data() {
    return {
      delay: 500,
      groupMenuVisible: false,
      showList: false,
      throttle: false,
      timer: null
    };
  },
  computed: {
    /**
     * Get available members
     */
    availableMembers() {
      return this.team.filter(({ id: userId = null }) => !this.members.map(({ id = null }) => id).includes(userId));
    }
  },
  methods: {
    /**
     * Hanlde group name change
     * @param {object} event - event object
     * @returns {void}
     */
    handleInput(event) {
      const { target: { value = '' } = {} } = event;
      if (!this.throttle && this.isCreated) {
        this.timer = setTimeout(() => {
          clearTimeout(this.timer);
          this.$emit('handle-delayed-rename', this.groupId, this.name);
          this.throttle = false;
          this.timer = null;
        }, this.delay);
      }
      this.throttle = true;
      return this.$emit('handle-rename', this.groupId, value);
    },
    /**
     * Delete group
     * @param {number|string} groupId - group ID
     */
    deleteGroup(groupId = '') {
      // close the group modal first
      this.groupMenuVisible = false;

      // and then delete the group
      return this.$emit('delete-group', groupId);
    },
    /**
     * Add a new group member
     * @param {number|string} userId - user ID
     * @returns {void}
     */
    addMember(userId = '') {
      this.showList = false;
      return this.$emit('add-member', this.groupId, Number(userId));
    },
    /**
     * Show / hide available members list
     * @returns {boolean}
     */
    toggleAvailableMembers() {
      return (this.showList = !this.showList);
    },
    /**
     * Show / hide group menu
     * @returns {boolean}
     */
    openGroupMenu() {
      this.groupMenuVisible = true;
      this.$nextTick(function() {
        this.$refs.groupMenu.focus();
      });
    }
  }
};
</script>

<style scoped lang="scss">
.group-menu {
  outline: none;
  display: flex;
  position: absolute;
  width: 250px;
  padding: 15px 0;
  background: #ffffff;
  border: 1px solid #dfe1e5;
  border-radius: 10px;
}

.group-container {
  width: 510px;
  background: #ffffff;
  border: 1px solid #dfe1e5;
  border-radius: 10px;
  padding: 30px;
  margin-top: 15px;

  .group-name-row {
    display: flex;
    flex-direction: row;
    align-items: center;
  }

  .group-name {
    flex: 1;
    color: $colorTitle;
    border: none;
    outline: none !important;
    font-weight: 600;
    font-size: 24px;
    line-height: 20px;
    letter-spacing: -0.01em;

    &::placeholder {
      color: $colorPlaceholder;
    }
  }

  .group-3-dots {
    width: 26px;
    height: 6px;
    cursor: pointer;
  }

  .group-separator {
    height: 1.5px;
    background-color: #c2c7cf;
    margin-top: 15px;
  }

  .group-add-team {
    position: relative;

    .group-add-team-button {
      font-weight: 500;
      font-size: 24px;
      line-height: 20px;
      letter-spacing: -0.01em;
      color: $colorPlaceholder;
      margin-top: 25.5px;
      border: none;
      outline: none;
      cursor: pointer;
      background-color: white;
      &.disabled {
        color: rgba(152, 160, 173, 0.2);
      }
    }

    .team-list {
      position: absolute;
      width: 360px;
      padding: 15px 0;
      background: #ffffff;
      border: 1px solid #dfe1e5;
      border-radius: 10px;
    }
  }

  .team-item {
    width: 100%;
    height: 55px;
    display: flex;
    flex-direction: row;
    align-items: center;

    &.list {
      padding: 0 25px;
      cursor: pointer;

      &:hover {
        background-color: $colorBack;
      }
    }

    .team-item-avatar {
      width: 35px;
      height: 35px;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      border-radius: 20px;
      font-weight: 500;
      font-size: 20px;
      line-height: 15px;
      text-align: center;
      letter-spacing: 0.03em;
      color: #ffffff;
    }
    .team-item-name {
      flex: 1;
      font-weight: 500;
      font-size: 24px;
      line-height: 20px;
      letter-spacing: -0.01em;
      margin: 0 15px;
      color: $colorTitle;
    }
    .team-item-delete {
      cursor: pointer;
    }
  }
}
.error-message {
  margin-top: 15px;
  color: red;
}
</style>
